<template>
    <ResponsiveDialog
        :value="value"
        @input="$emit('input', $event)"
        @header-touchstart="onHeaderTouchStart"
        @header-touchend="onHeaderTouchEnd"
        :header-title="trainer ? trainer.name : ''"
        :header-image="trainer && trainer.img ? require('@/assets/pic/trainer/' + trainer.img) : ''"
        header-icon="✉️"
        :bottom-inset="54"
        :max-width="480"
    >
        <div class="chat-popup-body">
            <div class="chat-popup-messages" ref="messagesEl">
                <div
                    v-for="m in messages"
                    :key="m.id"
                    class="chat-popup-row"
                    :class="m.senderId === myTrainerId ? 'chat-popup-row--own' : 'chat-popup-row--other'"
                >
                    <div class="chat-popup-bubble">{{ m.text }}</div>
                    <div class="chat-popup-time">{{ messageTime(m.created) }}</div>
                </div>
                <div v-if="!loading && messages.length === 0" class="chat-popup-empty">{{ $t('journey.playerChat.emptyMessage') }}</div>
            </div>
            <div class="chat-popup-input-row">
                <v-text-field
                    v-model="draft"
                    :placeholder="$t('journey.playerChat.inputPlaceholder')"
                    maxlength="500"
                    hide-details
                    dense
                    @keyup.enter="send"
                />
                <v-btn color="secondary" :disabled="!draft.trim()" @click="send">{{ $t('journey.playerChat.sendButton') }}</v-btn>
            </div>
        </div>
    </ResponsiveDialog>
</template>

<script>
    // Fullscreen chat popup pro zprávy mezi hráči. Historie se načte při
    // otevření (watch na "value"). Žádné pollování - příchozí zpráva od
    // druhé strany během otevřeného popupu se přesto objeví živě přes
    // Mercure push (viz onPlayerMessageEvent níže).
    import {mapActions} from 'vuex';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import mercureClient from '@/utils/mercureClient';
    import {messageTime} from '@/utils/formatTime';

    export default {
        name: 'PlayerChatPopup',
        components: {ResponsiveDialog},
        props: {
            value: {type: Boolean, default: false},
            trainer: {type: Object, default: null},
            myTrainerId: {type: Number, default: null},
        },
        data() {
            return {
                messages: [],
                draft: '',
                loading: false,
                touchStartY: null,
                unsubscribePlayerMessage: null,
            }
        },
        created() {
            this.unsubscribePlayerMessage = mercureClient.onMessage('player_message', this.onPlayerMessageEvent);
        },
        beforeDestroy() {
            if (this.unsubscribePlayerMessage) {
                this.unsubscribePlayerMessage();
            }
        },
        watch: {
            value(open) {
                if (open) {
                    this.loadHistory();
                }
            },
        },
        methods: {
            ...mapActions({
                loadPlayerMessagesAction: 'trainer/loadPlayerMessages',
                sendPlayerMessageAction: 'trainer/sendPlayerMessage',
            }),
            loadHistory() {
                if (!this.trainer) {
                    return;
                }
                this.loading = true;
                this.loadPlayerMessagesAction({trainerId: this.myTrainerId, otherId: this.trainer.id})
                    .then((data) => {
                        this.messages = data || [];
                        this.loading = false;
                        this.scrollToBottom();
                    });
            },
            send() {
                const text = this.draft.trim();
                if (!text || !this.trainer) {
                    return;
                }
                this.draft = '';
                this.sendPlayerMessageAction({receiverId: this.trainer.id, text})
                    .then((message) => {
                        this.messages.push(message);
                        this.scrollToBottom();
                    });
            },
            // Živé doplnění otevřeného chatu při příchozí Mercure události -
            // jen pro právě otevřenou konverzaci s tímhle odesílatelem, žádný
            // zvuk navíc (ten už zazněl na úrovni globálního listeneru v App.vue).
            onPlayerMessageEvent(payload) {
                if (!this.trainer || payload.sender.id !== this.trainer.id) {
                    return;
                }
                this.messages.push(payload.message);
                this.scrollToBottom();
            },
            scrollToBottom() {
                this.$nextTick(() => {
                    const el = this.$refs.messagesEl;
                    if (el) {
                        el.scrollTop = el.scrollHeight;
                    }
                });
            },
            messageTime,
            // Swipe dolů zavírá popup - záměrně jen na hlavičce
            // (ResponsiveDialog header-touchstart/-touchend), NE na
            // .chat-popup-messages, kde by to kolidovalo s běžným scrollem
            // historie. Práh 60px (vyšší než 40px u horizontálního swipu
            // mezi záložkami v TeamBottomSheet.vue) - svislé "swipe to
            // dismiss" chce víc záměrného tažení, ať nezavírá omylem.
            onHeaderTouchStart(e) {
                this.touchStartY = e.changedTouches[0].clientY;
            },
            onHeaderTouchEnd(e) {
                if (this.touchStartY === null) {
                    return;
                }
                const deltaY = e.changedTouches[0].clientY - this.touchStartY;
                this.touchStartY = null;
                if (deltaY > 60) {
                    this.$emit('input', false);
                }
            },
        },
    }
</script>

<style scoped>
.chat-popup-body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    /* Rezerva pro plovoucí zavírací křížek (ResponsiveDialog), stejná
       hodnota jako ostatní next/ popupy - bez ní sedělo tlačítko Send
       přesně pod křížkem. */
    padding: 12px 16px var(--responsive-dialog-close-clearance) 16px;
}
.chat-popup-messages {
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.chat-popup-row {
    display: flex;
    flex-direction: column;
    max-width: 80%;
}
.chat-popup-row--own {
    align-self: flex-end;
    align-items: flex-end;
}
.chat-popup-row--other {
    align-self: flex-start;
    align-items: flex-start;
}
.chat-popup-bubble {
    border: 1px solid rgba(0,0,0,0.2);
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 14px;
    word-break: break-word;
}
.chat-popup-time {
    font-size: 11px;
    opacity: 0.6;
    margin-top: 2px;
}
.chat-popup-empty {
    margin: auto;
    opacity: 0.6;
    font-size: 13px;
}
.chat-popup-input-row {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 10px;
}
</style>
